import {
  BrickEventHandler,
  MessageConf,
  PluginRuntimeContext,
} from "@next-core/brick-types";
import {
  WebsocketMessageRequest,
  WebsocketMessageResponse,
} from "../websocket";
import {
  MessageBrickEventHandlerCallback,
  PluginWebSocketMessageEvent,
  PluginWebSocketMessageResponsePayload,
  PluginWebSocketMessageTopic,
} from "../websocket/interfaces";
import { BrickAndMessage } from "./LocationContext";
import { listenerFactory } from "../internal/bindListeners";
import { createWebSocket } from "../websocket/WebSocket";
import minimatch from "minimatch";
import { WebSocketService } from "../websocket/WebSocketService";
import { _internalApiMessageCloseHandler } from "./Runtime";
import { RuntimeBrick } from "./BrickNode";
import { getReadOnlyProxy } from "../internal/proxyFactories";

let messageDispatcher: MessageDispatcher;

export function createMessageDispatcher(): MessageDispatcher {
  messageDispatcher = new MessageDispatcher();

  return messageDispatcher;
}

export function getMessageDispatcher(): MessageDispatcher {
  return messageDispatcher;
}

export class MessageDispatcher {
  private ws: WebSocketService;
  private context: PluginRuntimeContext;
  private messages: Map<string, BrickAndMessage[]> = new Map<
    string,
    BrickAndMessage[]
  >();
  private messageCallbackHandlers: Map<
    string,
    MessageBrickEventHandlerCallback[]
  > = new Map();
  private channels: Map<string, string> = new Map();

  create(
    brickAndMessages: BrickAndMessage[],
    context: PluginRuntimeContext
  ): void {
    if (Array.isArray(brickAndMessages)) {
      for (const { brick, match, message, tplContextId } of brickAndMessages) {
        ([] as MessageConf[]).concat(message).forEach((massageConf) => {
          const channel = massageConf.channel;
          const handler = { brick, match, message, tplContextId };
          if (this.messages.has(channel)) {
            this.messages.set(channel, [
              ...this.messages.get(channel),
              handler,
            ]);
          } else {
            this.messages.set(channel, [handler]);
          }
        });
      }
    }
    this.context = context;
  }

  reset(): void {
    this.messages = new Map();
    this.messageCallbackHandlers = new Map();
  }

  subscribe(
    channel: string,
    topic: PluginWebSocketMessageTopic,
    callback?: MessageBrickEventHandlerCallback
  ): void {
    const req = new WebsocketMessageRequest(
      PluginWebSocketMessageEvent.SUB,
      topic
    );
    this.setMessageCallbackHandlers(req.identity, callback);

    if (this.ws?.state === "finished") {
      const { options, retryCount, state, readyState } = this.ws;

      this.handleError({
        options: getReadOnlyProxy(options),
        retryCount,
        state,
        readyState,
      });

      return;
    }

    // Prevent repeated subscriptions to the same topic.
    if (!this.channels.has(req.topic)) {
      this.channels.set(req.topic, channel);
      this.send(req.data);
    }
  }

  unsubscribe(
    channel: string,
    topic: PluginWebSocketMessageTopic,
    callback: MessageBrickEventHandlerCallback
  ): void {
    const t = this.getTopicByChannel(channel);
    if (!t) {
      // eslint-disable-next-line no-console
      console.error(`Message channel: "${channel}" not found. `);
      return;
    }
    const req = new WebsocketMessageRequest(
      PluginWebSocketMessageEvent.UNSUB,
      JSON.parse(t)
    );

    this.setMessageCallbackHandlers(req.identity, callback);
    this.send(req.data);
    this.channels.delete(t);
  }

  private getTopicByChannel(channel: string): string | undefined {
    return [...this.channels.entries()]
      .filter(([, v]) => v === channel)
      .map(([v]) => v)[0];
  }

  private send(data: string): void {
    if (!(this.ws instanceof WebSocketService)) {
      this.createWebsocketService();
    }
    this.ws.send(data);
  }

  private createWebsocketService(): void {
    this.ws = createWebSocket();
    this.ws.onError = (_event, data) => {
      this.handleError(data);
    };
    this.ws.onMessage = (message) => {
      this.reducer(message);
    };
    this.ws.onClose = (event: CloseEvent) => {
      this.onClose(event);
    };
  }

  handleError(
    data: Pick<
      WebSocketService,
      "options" | "retryCount" | "state" | "readyState"
    >
  ): void {
    const event = new CustomEvent("callback.error", {
      detail: {
        payload: { message: {} }, // for compatibility
        ...data,
      },
    });

    this.messageCallbackHandlers.forEach((callbacks) => {
      this.messageSubscribeCallbacksHandler(callbacks, "error", event);
    });
  }

  onClose(event: CloseEvent): void {
    _internalApiMessageCloseHandler(event);
  }

  setMessageCallbackHandlers(
    identity: string,
    callback: MessageBrickEventHandlerCallback
  ): void {
    if (this.messageCallbackHandlers.get(identity)) {
      this.messageCallbackHandlers.set(identity, [
        ...this.messageCallbackHandlers.get(identity),
        ...[].concat(callback).filter(Boolean),
      ]);
    } else {
      this.messageCallbackHandlers.set(
        identity,
        [].concat(callback).filter(Boolean)
      );
    }
  }

  private reducer(response: WebsocketMessageResponse): void {
    const { event, message, topic } = response;
    switch (event) {
      case PluginWebSocketMessageEvent.MESSAGE_PUSH: {
        this.messagePushHandler(topic, message?.payload);
        break;
      }
      case PluginWebSocketMessageEvent.SUB_SUCCESS: {
        this.messageSubscribeResponseEventHandler(
          response,
          "success",
          PluginWebSocketMessageEvent.SUB_SUCCESS
        );
        break;
      }
      case PluginWebSocketMessageEvent.SUB_FAILED: {
        this.messageSubscribeResponseEventHandler(
          response,
          "error",
          PluginWebSocketMessageEvent.SUB_FAILED
        );
        break;
      }
      case PluginWebSocketMessageEvent.UNSUB_SUCCESS: {
        this.messageSubscribeResponseEventHandler(
          response,
          "success",
          PluginWebSocketMessageEvent.UNSUB_SUCCESS
        );
        break;
      }
      case PluginWebSocketMessageEvent.UNSUB_FAILED: {
        this.messageSubscribeResponseEventHandler(
          response,
          "error",
          PluginWebSocketMessageEvent.UNSUB_FAILED
        );
        break;
      }
      default: {
        // eslint-disable-next-line no-console
        console.error("Unknown message event:", event);
      }
    }
  }

  private dispatch(
    event: CustomEvent,
    brick: RuntimeBrick,
    context: PluginRuntimeContext,
    handlers: BrickEventHandler[]
  ): void {
    for (const handler of handlers.filter(Boolean)) {
      listenerFactory(handler, context, brick)(event);
    }
  }

  private getMessageHandlersByTopic(
    topic: PluginWebSocketMessageTopic
  ): BrickAndMessage[] {
    const finder = [...this.channels.keys()].find((key) => {
      const t = JSON.parse(key);
      // Support glob expressions
      return t.system === topic.system && minimatch(topic.topic, t.topic);
    });

    return this.messages.get(this.channels.get(finder));
  }
  private messagePushHandler(
    topic: string,
    payload: PluginWebSocketMessageResponsePayload
  ): void {
    const handlers = this.getMessageHandlersByTopic(JSON.parse(topic));

    if (Array.isArray(handlers)) {
      for (const brickAndMessage of handlers) {
        const event = new CustomEvent(
          PluginWebSocketMessageEvent.MESSAGE_PUSH,
          {
            detail: payload?.message,
          }
        );

        this.dispatch(
          event,
          brickAndMessage.brick,
          {
            ...this.context,
            tplContextId: brickAndMessage.tplContextId,
          },
          ([] as MessageConf[])
            .concat(brickAndMessage.message)
            .reduce((prev, curr) => prev.concat(curr?.handlers), [])
        );
      }
    }
  }

  private messageSubscribeCallbacksHandler(
    callbacks: MessageBrickEventHandlerCallback[],
    method: "success" | "error",
    event: CustomEvent
  ): void {
    if (Array.isArray(callbacks)) {
      for (const handler of callbacks) {
        this.dispatch(
          event,
          handler.runtimeBrick,
          handler.context,
          [].concat(handler?.[method]).filter(Boolean)
        );
      }
    }
  }

  private messageSubscribeResponseEventHandler(
    response: WebsocketMessageResponse,
    method: "success" | "error",
    event: PluginWebSocketMessageEvent
  ): void {
    const callbacks = this.messageCallbackHandlers.get(response.identity);
    const e = new CustomEvent(event, {
      detail: response.message,
    });
    this.messageSubscribeCallbacksHandler(callbacks, method, e);
  }
}
